import './styles.scss'

import { memo, useCallback, useState } from 'react'
import { useSelector } from 'react-redux'

import * as Dialog from '@radix-ui/react-dialog'
import * as Tabs from '@radix-ui/react-tabs'

import { GameController } from '@Animation/game-control'
import { AppSettingsActions } from '@AppState/app-settings'
import { RootState, useAppDispatch } from '@AppState/redux-store'
import { DrawerState } from '@Components/expandable-drawer'
import { Settings } from '@Components/settings/settings'
import { TicketMode } from '@Components/ticket-mode/ticket-mode'

import { NAV_MODE, NAV_VALUES } from './types'
import Analytics from '@Util/analytics'
import { LeavingDrawer } from '@Components/ticket-mode/leaving-drawer/leaving-drawer'
import { TicketModeActions } from '@AppState/ticket-info'
import { TicketHistoryMode } from '@Components/ticket-history-mode/ticket-history-mode'
import { NavigationActions } from '@AppState/navigation'
import { ViewMode } from '@Components/watch-animation/view-mode'
import { AnimationActions } from '@AppState/animation'
import { AnalyticsMode } from '@Components/analytics-mode/analytics-mode'
import { ReactSVG } from 'react-svg'

const CLASS_NAME = 'navigation-bar'

export const NavigationBar = memo(() => {
  const [settingsState, setSettingsState] = useState<DrawerState>(DrawerState.CLOSED)
  const [leavingDrawerState, setLeavingDrawerState] = useState<DrawerState>(DrawerState.CLOSED)
  const activeMode = useSelector((state: RootState) => state.navigation.activeMode)
  const ticketHistoryEnabled = useSelector(
    (state: RootState) => state.hostConfig.config?.features?.ticketHistory,
  )
  const analyticsPageEnabled = useSelector(
    (state: RootState) => state.hostConfig.config?.features?.analyticsPage,
  )
  const [nextMode, setNextMode] = useState<NAV_MODE>(activeMode)
  const [translationDistance, setTranslationDistance] = useState<number>(0)
  const [pillRef, setPillRef] = useState<HTMLButtonElement | null>(null)
  const dispatch = useAppDispatch()

  const useRefCallback = useCallback(
    (node: HTMLButtonElement) => {
      setPillRef(node)
      if (activeMode && node) {
        setTranslationDistance(activeMode.index * (node.scrollWidth + 16))
      }
    },
    [activeMode, setPillRef],
  )

  const onSettingsChanged = useCallback(
    (open: boolean) => {
      if (settingsState === DrawerState.OPEN && !open) {
        setSettingsState(DrawerState.CLOSING)
        setTimeout(() => {
          setSettingsState(DrawerState.CLOSED)
          dispatch(AppSettingsActions.updateMenuOpen(false))

          dispatch(AnimationActions.setPaused(false))
        }, 200)
      } else if (open) {
        dispatch(AnimationActions.setPaused(true))
        setSettingsState(DrawerState.OPEN)
        dispatch(AppSettingsActions.updateMenuOpen(true))
      }
    },
    [settingsState, dispatch],
  )

  const leavingDrawerChangeHandler = useCallback(
    (open: boolean) => {
      setLeavingDrawerState(open ? DrawerState.OPEN : DrawerState.CLOSED)
      dispatch(AnimationActions.setPaused(open))
    },
    [dispatch],
  )

  const onTabChange = useCallback(
    (newMode: string) => {
      if (
        activeMode === NAV_VALUES.TICKET_MODE &&
        (newMode === NAV_VALUES.VIEW_MODE.key ||
          newMode === NAV_VALUES.TICKET_HISTORY_MODE.key ||
          newMode === NAV_VALUES.ANALYTICS_MODE.key) &&
        leavingDrawerState === DrawerState.CLOSED
      ) {
        dispatch(AnimationActions.setPaused(true))
        leavingDrawerChangeHandler(true)
        setNextMode(NAV_VALUES[newMode])
        return
      }

      if (leavingDrawerState === DrawerState.OPEN) {
        leavingDrawerChangeHandler(false)
        dispatch(TicketModeActions.updateTicketForm(null))
      }

      if (activeMode !== NAV_VALUES.TICKET_MODE && newMode === NAV_VALUES.TICKET_MODE.key) {
        dispatch(TicketModeActions.updateTicketForm(null))
        dispatch(TicketModeActions.resetFetchState())
      }

      const newModeObject = NAV_VALUES[newMode]
      setTranslationDistance(newModeObject.index * ((pillRef?.scrollWidth ?? 0) + 16))
      dispatch(NavigationActions.updateActiveMode(newModeObject))
      GameController.stopDrawAnimation()
      Analytics.trackPageView(newMode)
    },
    [pillRef?.scrollWidth, leavingDrawerState, activeMode, leavingDrawerChangeHandler, dispatch],
  )

  return (
    <Dialog.Root
      open={settingsState !== DrawerState.CLOSED}
      onOpenChange={(open: boolean) => onSettingsChanged(open)}>
      {leavingDrawerState === DrawerState.OPEN ? (
        <LeavingDrawer
          isOpen={leavingDrawerState === DrawerState.OPEN}
          onOpenChange={leavingDrawerChangeHandler}
          onLeaveSelect={() => onTabChange(nextMode.key)}
        />
      ) : null}
      <Tabs.Root
        className={CLASS_NAME}
        activationMode="manual"
        value={activeMode.key}
        onValueChange={onTabChange}>
        <nav className={`${CLASS_NAME}__nav`}>
          <Tabs.List className={`${CLASS_NAME}__list`}>
            <Tabs.Trigger
              className={`${CLASS_NAME}__item`}
              value={NAV_VALUES.VIEW_MODE.key}
              aria-label={'View Mode'}
              ref={useRefCallback}>
              <ReactSVG
                src={`/icons/view-mode.svg`}
                className={`${CLASS_NAME}__icon`}
              />
            </Tabs.Trigger>
            <Tabs.Trigger
              className={`${CLASS_NAME}__item`}
              value={NAV_VALUES.TICKET_MODE.key}
              aria-label={'Ticket Mode'}>
              <ReactSVG
                src={`/icons/ticket-mode.svg`}
                className={`${CLASS_NAME}__icon`}
              />
            </Tabs.Trigger>
            {ticketHistoryEnabled ? (
              <Tabs.Trigger
                className={`${CLASS_NAME}__item`}
                value={NAV_VALUES.TICKET_HISTORY_MODE.key}
                aria-label={'Ticket History Mode'}>
                <ReactSVG
                  src={`/icons/ticket-history-mode.svg`}
                  className={`${CLASS_NAME}__icon`}
                />
              </Tabs.Trigger>
            ) : null}
            {analyticsPageEnabled ? (
              <Tabs.Trigger
                className={`${CLASS_NAME}__item`}
                value={NAV_VALUES.ANALYTICS_MODE.key}
                aria-label={'Analytics'}>
                <ReactSVG
                  src={`/icons/analytics-mode.svg`}
                  className={`${CLASS_NAME}__icon`}
                />
              </Tabs.Trigger>
            ) : null}
            <Dialog.Trigger className={`${CLASS_NAME}__item`} aria-label={'Settings'}>
              <ReactSVG
                  src={`/icons/settings.svg`}
                  className={`${CLASS_NAME}__icon`}
                />
            </Dialog.Trigger>
            <Settings
              drawerState={settingsState}
              onOpenChange={(open: boolean) => onSettingsChanged(open)}
            />
            <span
              className={`${CLASS_NAME}__pill`}
              style={{
                width: pillRef?.clientWidth,
                transform: `translatex(${translationDistance}px)`,
              }}></span>
          </Tabs.List>
        </nav>
        <Tabs.Content className={`${CLASS_NAME}__content`} value={NAV_VALUES.VIEW_MODE.key}>
          <div className={`${CLASS_NAME}__content-body`}>
            <ViewMode />
          </div>
        </Tabs.Content>
        <Tabs.Content className={`${CLASS_NAME}__content`} value={NAV_VALUES.TICKET_MODE.key}>
          <TicketMode />
        </Tabs.Content>
        {ticketHistoryEnabled ? (
          <Tabs.Content
            className={`${CLASS_NAME}__content`}
            value={NAV_VALUES.TICKET_HISTORY_MODE.key}>
            <TicketHistoryMode />
          </Tabs.Content>
        ) : null}
        {analyticsPageEnabled ? (
          <Tabs.Content className={`${CLASS_NAME}__content`} value={NAV_VALUES.ANALYTICS_MODE.key}>
            <AnalyticsMode />
          </Tabs.Content>
        ) : null}
      </Tabs.Root>
    </Dialog.Root>
  )
})
